import React, { useState, useEffect, CSSProperties } from "react";
import { MultiSelectValidationRules } from "types";

interface MultiSelectOption {
  label: string;
  value: string;
}

interface MultiSelectProps {
  label: string;
  name: string;
  values: string[];
  onChange: (values: string[]) => void;
  options: MultiSelectOption[];
  placeholder?: string;
  rules?: MultiSelectValidationRules;
  className?: string;
  style?: CSSProperties;
}

const MultiSelect: React.FC<MultiSelectProps> = ({
  label,
  name,
  values,
  onChange,
  options,
  placeholder = "Select options",
  rules = {},
  className = "",
  style = {},
}) => {
  const [error, setError] = useState<string | null>(null);

  const validate = (vals: string[]): string | null => {
    if (rules.required && vals.length === 0) {
      return typeof rules.required === "string"
        ? rules.required
        : `${label} is required`;
    }
    if (rules.validate) {
      const result = rules.validate(vals);
      if (result !== true) {
        return typeof result === "string" ? result : `${label} is invalid`;
      }
    }
    return null;
  };

  useEffect(() => {
    setError(validate(values));
  }, [values]);

  const handleChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const selected = Array.from(
      e.target.selectedOptions,
      (option) => option.value
    );
    onChange(selected);
  };

  return (
    <div className={className} style={style}>
      <label className="block mb-2 text-sm font-medium text-[#747171]">
        {label}
      </label>
      <select
        name={name}
        multiple
        value={values}
        onChange={handleChange}
        aria-invalid={!!error}
        aria-describedby={error ? `${name}-error` : undefined}
        className={`text-sm rounded-lg block w-full p-2.5 text-gray-900 dark:text-white 
                    bg-[#F2F2F2] focus:outline-none border-none`.trim()}
      >
        {placeholder && values.length === 0 && (
          <option disabled value="">
            {placeholder}
          </option>
        )}
        {options.map((opt) => (
          <option key={opt.value} value={opt.value}>
            {opt.label}
          </option>
        ))}
      </select>
      {error && (
        <p
          id={`${name}-error`}
          className="mt-1 text-sm text-red-600 dark:text-red-500"
        >
          {error}
        </p>
      )}
    </div>
  );
};

export default MultiSelect;

